import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useEffect, useState } from "react";
import { Button } from "../../components/ui/Button";
import { Dialog } from "../../components/ui/Dialog";
import { Field, TextInput } from "../../components/ui/Field";
import { toast } from "../../components/ui/Toast";
import { ApiError } from "../../lib/api/client";
import { api } from "../../lib/api/endpoints";
import type { Item, ItemPatch } from "../../lib/api/models";
import { refreshInventory } from "../../lib/queries";

/** What the two refusals of a save (409) say in the app's own words. */
const REFUSED: Record<string, string> = {
  field_from_gorelo: "This field comes from Gorelo. Change it there.",
  client_unlinked: "This client is not linked to Gorelo, so its items cannot be changed.",
};

const AREA =
  "min-h-20 w-full rounded-md border border-line bg-surface px-2.5 py-2 text-base text-ink placeholder:text-ink-3 focus:border-green focus:outline-none";

/**
 * Edits the fields Docs owns (Gorelo sync §4.3): the location and the notes of every item, and for a custom asset
 * also the manufacturer, the model and the warranty start, which Gorelo does not hold there. Everything else is
 * changed in Gorelo. Nothing secret is stored here: no passwords, no IP or MAC addresses.
 */
export function ItemDialog({
  clientId,
  item,
  open,
  onClose,
}: {
  clientId: string;
  item: Item;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const custom = item.source === "custom";
  const [manufacturer, setManufacturer] = useState("");
  const [model, setModel] = useState("");
  const [warrantyStart, setWarrantyStart] = useState("");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // What the form was filled from: Save compares with this, not with a later refetch of the item.
  const [loaded, setLoaded] = useState<Item | null>(null);
  const base = loaded ?? item;

  // The form starts again when the dialog opens or shows another item, not when a refetch brings a new object.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `item` itself is left out on purpose
  useEffect(() => {
    if (!open) return;
    setLoaded(item);
    setManufacturer(item.manufacturer);
    setModel(item.model);
    setWarrantyStart(item.warrantyStart ?? "");
    setLocation(item.location);
    setNotes(item.notes);
    setFormError(null);
  }, [open, item.id]);

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // An incomplete date reads as "", like an emptied one: only the browser can tell them apart.
    const date = e.currentTarget.elements.namedItem("warrantyStart");
    if (date instanceof HTMLInputElement && date.validity.badInput) {
      setFormError("The warranty start is not a complete date.");
      return;
    }
    // Only what differs from the item is sent, so a colleague's change to another field is not overwritten.
    const patch: ItemPatch = {};
    // Text left as stored is not rewritten, also when the stored text has spaces around it.
    const changed = (input: string, stored: string) => input !== stored && input.trim() !== stored;
    if (changed(location, base.location)) patch.location = location.trim();
    if (changed(notes, base.notes)) patch.notes = notes.trim();
    if (custom) {
      if (changed(manufacturer, base.manufacturer)) patch.manufacturer = manufacturer.trim();
      if (changed(model, base.model)) patch.model = model.trim();
      if (warrantyStart !== (base.warrantyStart ?? "")) patch.warrantyStart = warrantyStart || null;
    }
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    setBusy(true);
    try {
      await api.items.update(item.id, patch);
      toast(`“${item.name}” saved.`, "success");
      await refreshInventory(queryClient, clientId);
      onClose();
    } catch (err) {
      const refused = err instanceof ApiError && err.status === 409 ? REFUSED[err.code] : undefined;
      setFormError(refused ?? (err instanceof Error ? err.message : "The change could not be saved."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title={`Edit ${item.name}`} size="md">
      <form onSubmit={submit} noValidate className="grid gap-4">
        {formError ? (
          <p role="alert" className="rounded-md bg-danger-wash px-3 py-2 text-sm text-danger">
            {formError}
          </p>
        ) : null}
        <p className="text-sm text-ink-3">
          {custom
            ? "Name, kind, serial number, warranty end and site come from Gorelo. Change them there."
            : "Everything except the location and the notes comes from Gorelo. Change it there."}
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          {custom ? (
            <>
              <Field label="Manufacturer">
                {(id) => (
                  <TextInput
                    id={id}
                    maxLength={120}
                    value={manufacturer}
                    onChange={(e) => setManufacturer(e.target.value)}
                  />
                )}
              </Field>
              <Field label="Model">
                {(id) => <TextInput id={id} maxLength={120} value={model} onChange={(e) => setModel(e.target.value)} />}
              </Field>
              <Field label="Warranty start">
                {(id) => (
                  <TextInput
                    id={id}
                    name="warrantyStart"
                    type="date"
                    min="1990-01-01"
                    max="2100-12-31"
                    value={warrantyStart}
                    onChange={(e) => setWarrantyStart(e.target.value)}
                  />
                )}
              </Field>
            </>
          ) : null}
          <Field label="Location">
            {(id) => (
              <TextInput
                id={id}
                maxLength={300}
                value={location}
                placeholder="Server room · rack A"
                onChange={(e) => setLocation(e.target.value)}
              />
            )}
          </Field>
        </div>
        <Field label="Notes" hint="Anything a colleague needs to know about it. No passwords or other secrets.">
          {(id, describedBy) => (
            <textarea
              id={id}
              aria-describedby={describedBy}
              maxLength={4000}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={AREA}
            />
          )}
        </Field>
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={busy}>
            Save
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
